import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import UserAccountModal from './UserAccountModal';

const { FiShield, FiUsers, FiPlus, FiCheckCircle, FiSearch } = FiIcons;

const AdminConsole = () => {
  const { allUsers, loading } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = allUsers.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
            <SafeIcon icon={FiShield} className="text-blue-600" />
            Admin Console
          </h1>
          <p className="text-slate-400 font-medium">Platform-wide user oversight and system health.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-3xl font-black shadow-xl shadow-blue-100 flex items-center gap-3 hover:bg-blue-700 transition-all hover:-translate-y-1"
        >
          <SafeIcon icon={FiPlus} /> New Account
        </button>
      </div>

      <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
        <div className="relative w-full">
          <SafeIcon icon={FiSearch} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
          <input 
            placeholder="Search registered accounts..." 
            className="w-full pl-14 pr-6 py-4 bg-slate-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
          <h2 className="font-black text-slate-900 flex items-center gap-2">
            <SafeIcon icon={FiUsers} /> 
            Registered Accounts ({allUsers.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50">
                <th className="px-8 py-6">User</th>
                <th className="px-8 py-6">Company</th>
                <th className="px-8 py-6">Role</th>
                <th className="px-8 py-6">Joined</th>
                <th className="px-8 py-6">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-4">
                      <img 
                        src={u.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop'} 
                        className="w-10 h-10 rounded-2xl border-2 border-white shadow-sm" 
                      />
                      <div>
                        <p className="font-black text-slate-900 text-sm tracking-tight">{u.name}</p>
                        <p className="text-xs text-slate-400 font-medium">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6 text-sm font-bold text-slate-600">{u.company || 'Not set'}</td>
                  <td className="px-8 py-6">
                    <span className={`text-[9px] font-black px-3 py-1.5 rounded-xl uppercase tracking-widest ${u.role === 'SUPER ADMIN' ? 'bg-purple-100 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-8 py-6 text-xs text-slate-400 font-bold">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-8 py-6">
                    <span className="flex items-center gap-1.5 text-[10px] font-black text-green-500 uppercase tracking-tighter">
                      <SafeIcon icon={FiCheckCircle} /> Active
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="py-20 text-center">
              <SafeIcon icon={FiUsers} className="text-6xl text-slate-100 mx-auto mb-4" />
              <p className="text-slate-400 font-bold">No matching accounts found.</p>
            </div>
          )}
        </div>
      </div>

      <UserAccountModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AdminConsole;